'use client';

import { useEffect, useRef } from 'react';
import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useTranslations } from 'next-intl';
import styles from './SecPlatformPortfolio.module.scss';

type Platform = {
  key: string;
  name: string;
  graySrc: string;
  blackSrc: string;
};

const PLATFORMS: Platform[] = [
  {
    key: 'imweb',
    name: 'Imweb',
    graySrc: '/intro/imweb_g.svg',
    blackSrc: '/intro/imweb_b.svg',
  },
  {
    key: 'ably',
    name: 'ABLY',
    graySrc: '/intro/ably_g.svg',
    blackSrc: '/intro/ably_b.svg',
  },
  {
    key: 'zigzag',
    name: 'ZIGZAG',
    graySrc: '/intro/zigzag_g.svg',
    blackSrc: '/intro/zigzag_b.svg',
  },
  {
    key: 'naver',
    name: 'Naver SmartStore',
    graySrc: '/intro/naver_g.svg',
    blackSrc: '/intro/naver_b.svg',
  },
  {
    key: 'cafe24',
    name: 'Cafe24',
    graySrc: '/intro/cafe24_g.svg',
    blackSrc: '/intro/cafe24_b.svg',
  },
  {
    key: 'sixshop',
    name: 'Sixshop',
    graySrc: '/intro/sixshop_g.svg',
    blackSrc: '/intro/sixshop_b.svg',
  },
  {
    key: '29cm',
    name: '29CM',
    graySrc: '/intro/29cm_g.svg',
    blackSrc: '/intro/29cm_b.svg',
  },
];

const LOGO_WIDTH = 180;
const LOGO_STRIDE = 240;
const TOTAL_CYCLE = PLATFORMS.length * LOGO_STRIDE;
const CYCLE_DURATION_MS = 28000;
const FOCAL_FALLOFF = 2.5;
const SCALE_MIN = 0.94;
const SCALE_MAX = 1.0266;
const OPACITY_MIN = 0.16;
const OPACITY_MAX = 1;
const GRAY_LAYER_MIN = 0.4166;
const BLACK_LAYER_MAX = 0.8333;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * t;
}

export function SecPlatformPortfolio() {
  const t = useTranslations('intro');
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const tileRefs = useRef<Array<HTMLDivElement | null>>([]);
  const grayRefs = useRef<Array<HTMLImageElement | null>>([]);
  const blackRefs = useRef<Array<HTMLImageElement | null>>([]);

  useEffect(() => {
    const carousel = carouselRef.current;
    const row = rowRef.current;
    if (!carousel || !row) {
      return undefined;
    }

    let containerWidth = carousel.clientWidth;
    const handleResize = () => {
      containerWidth = carousel.clientWidth;
    };
    window.addEventListener('resize', handleResize);

    const startTime = performance.now();
    let rafId = 0;
    let running = false;
    let inView = false;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = (elapsed % CYCLE_DURATION_MS) / CYCLE_DURATION_MS;
      const rowOffset = -progress * TOTAL_CYCLE;

      row.style.transform = `translate3d(${rowOffset}px, 0, 0)`;

      const centerX = containerWidth / 2;

      tileRefs.current.forEach((tile, i) => {
        if (!tile) {
          return;
        }
        const baseX = i * LOGO_STRIDE;
        const absX = baseX + rowOffset;
        const logoCenter = absX + LOGO_WIDTH / 2;
        const distNorm =
          Math.abs(logoCenter - centerX) / Math.max(containerWidth, 1);
        const focal = clamp(1 - distNorm * FOCAL_FALLOFF, 0, 1);

        const scale = lerp(SCALE_MIN, SCALE_MAX, focal);
        const opacity = lerp(OPACITY_MIN, OPACITY_MAX, focal);

        tile.style.transform = `translate3d(0, 0, 0) scale(${scale})`;
        tile.style.opacity = String(opacity);

        const grayImg = grayRefs.current[i];
        if (grayImg) {
          grayImg.style.opacity = String(lerp(1, GRAY_LAYER_MIN, focal));
        }
        const blackImg = blackRefs.current[i];
        if (blackImg) {
          blackImg.style.opacity = String(lerp(0, BLACK_LAYER_MAX, focal));
        }
      });

      rafId = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (running) {
        return;
      }
      running = true;
      rafId = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      if (!running) {
        return;
      }
      running = false;
      window.cancelAnimationFrame(rafId);
    };

    const syncPlayback = () => {
      if (inView && document.visibilityState === 'visible') {
        start();
      } else {
        stop();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        inView = entries[0]?.isIntersecting ?? false;
        syncPlayback();
      },
      { threshold: 0 },
    );
    observer.observe(carousel);

    const handleVisibility = () => {
      syncPlayback();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const tiles = [...PLATFORMS, ...PLATFORMS];

  return (
    <section
      id="platform-portfolio"
      data-section="platform-portfolio"
      className={styles.section}
    >
      <div className={styles.container}>
        <RevealOnScroll delayMs={60} y={16}>
          <div className={styles.headingBlock}>
            <h2 className={styles.heading}>{t('platform.heading')}</h2>
            <p className={styles.description}>{t('platform.description')}</p>
          </div>
        </RevealOnScroll>

        <RevealOnScroll
          delayMs={170}
          y={18}
          style={{ width: '100%', marginTop: '80px' }}
        >
          <div className={styles.carouselWrap}>
            <div className={styles.carouselInner}>
              <div className={styles.publicOverlay} aria-hidden="true">
                <p className={styles.publicLine}>public</p>
                <p
                  className={`${styles.publicLine} ${styles.publicLineSecond}`}
                >
                  to
                </p>
              </div>

              <div ref={carouselRef} className={styles.carousel}>
                <div className={styles.glow} aria-hidden="true" />
                <div className={styles.fadeLeft} aria-hidden="true" />
                <div className={styles.fadeRight} aria-hidden="true" />

                <div ref={rowRef} className={styles.logoRow}>
                  {tiles.map((platform, index) => {
                    const baseX = index * LOGO_STRIDE;
                    return (
                      <div
                        key={`${platform.key}-${index}`}
                        className={styles.logoSlot}
                        style={{ left: `${baseX}px` }}
                      >
                        <div
                          ref={(node) => {
                            tileRefs.current[index] = node;
                          }}
                          className={styles.logoInner}
                          aria-hidden="true"
                        >
                          <div className={styles.logoStack}>
                            <img
                              ref={(node) => {
                                grayRefs.current[index] = node;
                              }}
                              alt=""
                              aria-hidden="true"
                              className={styles.logoLayer}
                              src={platform.graySrc}
                            />
                            <img
                              ref={(node) => {
                                blackRefs.current[index] = node;
                              }}
                              alt=""
                              aria-hidden="true"
                              className={`${styles.logoLayer} ${styles.logoLayerBlack}`}
                              src={platform.blackSrc}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
}
